/**
 * ProtocolsFormView - Create/Edit form view for protocols management modal.
 * Displays form fields for protocol name and instructions with back navigation.
 */

import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { DialogFooter } from '@heidi/design-system/dialog';
import { Input } from '@heidi/design-system/input';
import { Label } from '@heidi/design-system/label';
import { Textarea } from '@heidi/design-system/textarea';
import { TypographyCaption, TypographyH3 } from '@heidi/design-system/typography';
import { ArrowLeft } from 'lucide-react';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formFieldCharCountMessages } from './form-field-char-count.messages';
import { protocolDetailModalMessages } from './protocol-detail-modal.messages';
import {
  SHORTCUT_NAME_MAX_LENGTH,
  SHORTCUT_PROMPT_MAX_LENGTH,
} from './shortcut-constants';

export interface ProtocolsFormViewProps {
  mode: 'create' | 'edit';
  protocolLabel?: string;
  editLabel: string;
  editQuery: string;
  onLabelChange: (value: string) => void;
  onQueryChange: (value: string) => void;
  onBack: () => void;
  onSave: () => void;
  onDelete?: () => void;
}

export const ProtocolsFormView = memo(
  ({
    mode,
    protocolLabel,
    editLabel,
    editQuery,
    onLabelChange,
    onQueryChange,
    onBack,
    onSave,
    onDelete,
  }: ProtocolsFormViewProps) => {
    const intl = useIntl();
    const isQueryOverLimit = editQuery.length > SHORTCUT_PROMPT_MAX_LENGTH;
    const isNameOverLimit = editLabel.length > SHORTCUT_NAME_MAX_LENGTH;
    const isFormValid =
      editLabel.trim() && editQuery.trim() && !isQueryOverLimit && !isNameOverLimit;

    const backAriaLabel = intl.formatMessage({
      defaultMessage: 'Back to protocols',
      id: '1E8o+s+TiT',
      description:
        'Accessibility label for leaving the protocol form and returning to the list',
    });

    const namePlaceholder = intl.formatMessage(
      protocolDetailModalMessages.namePlaceholder
    );

    const instructionsPlaceholder = intl.formatMessage(
      protocolDetailModalMessages.instructionsPlaceholder
    );

    const nameCharCount = intl.formatMessage(formFieldCharCountMessages.charCount, {
      current: editLabel.length,
      max: SHORTCUT_NAME_MAX_LENGTH,
    });

    const queryCharCount = intl.formatMessage(formFieldCharCountMessages.charCount, {
      current: editQuery.length,
      max: SHORTCUT_PROMPT_MAX_LENGTH,
    });

    return (
      <>
        {/* Header with back button */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-md',
                'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                'transition-colors duration-150'
              )}
              aria-label={backAriaLabel}
            >
              <ArrowLeft size={18} className="rtl:-scale-x-100" />
            </button>
            <TypographyH3 className="font-exposure tracking-tight text-text-primary">
              <FormattedMessage
                defaultMessage="{mode, select, create {/ New Protocol} edit {/ {label}} other {/ {label}}}"
                id="CIJMMR/1a+"
                values={{ mode, label: protocolLabel ?? '' }}
                description="Screen title for creating or editing a custom protocol; leading slash matches app visual style"
              />
            </TypographyH3>
          </div>
          <TypographyCaption className="pl-10 text-text-tertiary">
            <FormattedMessage
              defaultMessage="{mode, select, create {Create your protocol} edit {Edit your protocol} other {Edit your protocol}}"
              id="vuZDpomJ2h"
              values={{ mode }}
              description="Subtitle under the protocol form heading for create vs edit"
            />
          </TypographyCaption>
        </div>

        {/* Form fields */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="protocol-name" className="font-medium text-text-secondary">
              <FormattedMessage {...protocolDetailModalMessages.protocolName} />
            </Label>
            <Input
              id="protocol-name"
              value={editLabel}
              onChange={(e) => onLabelChange(e.target.value)}
              placeholder={namePlaceholder}
              autoFocus={mode === 'create'}
            />
            <TypographyCaption
              className={cn(
                'self-end',
                isNameOverLimit ? 'text-validation-destructive' : 'text-text-tertiary'
              )}
            >
              {nameCharCount}
            </TypographyCaption>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="protocol-query" className="font-medium text-text-secondary">
              <FormattedMessage {...protocolDetailModalMessages.protocolInstructions} />
            </Label>
            <Textarea
              id="protocol-query"
              value={editQuery}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={instructionsPlaceholder}
              className={cn(
                'min-h-[120px] resize-none',
                isQueryOverLimit && 'border-validation-destructive'
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.metaKey) {
                  e.preventDefault();
                  if (isFormValid) onSave();
                }
              }}
            />
            <TypographyCaption
              className={cn(
                'self-end',
                isQueryOverLimit ? 'text-validation-destructive' : 'text-text-tertiary'
              )}
            >
              {queryCharCount}
            </TypographyCaption>
          </div>
        </div>

        {/* Footer actions */}
        <DialogFooter className={cn(mode === 'edit' && 'justify-between')}>
          {mode === 'edit' && onDelete && (
            <Button size="lg" variant="destructive_soft" onClick={onDelete}>
              <FormattedMessage {...protocolDetailModalMessages.delete} />
            </Button>
          )}
          <div className="flex gap-2">
            <Button size="lg" variant="ghost" onClick={onBack}>
              <FormattedMessage {...protocolDetailModalMessages.cancel} />
            </Button>
            <Button size="lg" onClick={onSave} disabled={!isFormValid}>
              <FormattedMessage {...protocolDetailModalMessages.save} />
            </Button>
          </div>
        </DialogFooter>
      </>
    );
  }
);

ProtocolsFormView.displayName = 'ProtocolsFormView';
